import { LibraryCache } from 'base/library_cache';
import {
  CardStyle,
  CardStyleRadiusOption,
  UiStyle,
  UiStyleRadiusOption,
} from 'base/styleguide/model';
import { AlternateTarget, ColorVariable } from 'base/styleguide/types';
import { extractTreeByName, getNodeName } from 'base/webflow/data_attributes';
import {
  isReplaceableTextNode,
  textContentsOf,
} from 'base/webflow/replaceable_content';
import { ComponentStyle, NestedNode } from 'base/webflow/schema';
import { getSlotInfoAtNode } from 'blocks/component_renderer/slots';
import { ElementInstanceState, SectionState } from 'blocks/editor/model';
import { styleGuideStyles } from './webflow/style_guide';

export type ReactStyleGuide = {
  colors: ReadonlyMap<ColorVariable, string>;
  headingFont?: {
    family: string;
    rule: string;
  };
  bodyFont?: {
    family: string;
    rule: string;
  };
  typeSettings: {
    size: 'small' | 'large' | 'regular';
    weight: 'medium' | 'bold' | 'regular';
    style: 'default' | 'spacious' | 'uppercase';
  };
  buttonStyle: UiStyle;
  buttonRadius: UiStyleRadiusOption;
  cardStyle: CardStyle;
  cardRadius: CardStyleRadiusOption;
  mode: 'light' | 'dark';
};

export type ReactScheme = {
  background: ColorVariable;
  foreground: ColorVariable;
  text: ColorVariable;
  border: ColorVariable;
  alternate: AlternateTarget[];
  primaryButtonStyle: {
    isAlternate: boolean;
    color: ColorVariable;
    backgroundColor: ColorVariable;
  };
};

export type SimpleElementState = {
  name: string;
  texts?: [string, string][];
  slots?: Record<string, SimpleElementState[]>;
  lists?: Record<string, SimpleElementState[]>;
};

export type SimpleSectionState = {
  element: SimpleElementState;
  slug: string;
  scheme?: ReactScheme;
  styleGuide?: ReactStyleGuide;
};

const DELETE_SUFFIX = ' (Delete)';
const styleGuideDeleteClassIds = styleGuideStyles
  .filter((s) => s.name.endsWith(DELETE_SUFFIX))
  .map((s) => s._id);

/**
 * Removes any nodes that have a class suffixed in " (Delete)".
 *
 * In Relume Library components, this is used for nodes that contain instructions.
 */
export function removeDeleteSuffixedNodes(
  root: NestedNode,
  componentStyles: readonly ComponentStyle[],
): NestedNode {
  if (root.text) return root;

  const deleteClassIds = componentStyles
    .filter((s) => s.name.endsWith(DELETE_SUFFIX))
    .map((s) => s._id)
    .concat(styleGuideDeleteClassIds);
  if (deleteClassIds.length === 0) return root;

  function visit(n: NestedNode): NestedNode | NestedNode[] {
    if (n.text) {
      return n;
    }
    if (n.classes.some((id) => deleteClassIds.includes(id))) {
      return [];
    }
    return { ...n, children: n.children.flatMap(visit) };
  }

  return { ...root, children: root.children.flatMap(visit) };
}

/**
 * Extracts a simplified version of the element state for React integration.
 */
export async function extractSimpleElementState(
  elementState: ElementInstanceState,
  library: LibraryCache,
): Promise<SimpleElementState> {
  const extractText = (
    el: ElementInstanceState,
    node: NestedNode,
  ): Record<string, string> => {
    if (node.text) return {};

    const nodeName = getNodeName(node);
    const slotOverride = nodeName && el.slotOverrides.get(nodeName);
    // Don't traverse into slots
    if (slotOverride) return {};

    const text: Record<string, string> = {};
    if (!node.text && isReplaceableTextNode(node)) {
      text[node._id] = textContentsOf(node);
    }
    for (const child of node.children) {
      for (const [k, v] of Object.entries(extractText(el, child))) {
        text[k] = v;
      }
    }
    return text;
  };

  const processElement = async (
    element: ElementInstanceState,
    elementName?: string,
  ): Promise<SimpleElementState> => {
    const component = await library.getCachedPublicComponentJson(
      element.source.slug,
      { fetchIfMissing: true },
    )?.promise;
    if (!component) {
      throw new Error(`Component not found: ${element.source.slug}`);
    }

    const root = removeDeleteSuffixedNodes(
      extractTreeByName(component, element.source.rootName),
      component.payload.styles,
    );
    const name = element.source.rootName ?? elementName;
    if (name == null) {
      throw new Error('Element does not have a root name');
    }

    const textMap = new Map(
      Object.entries(extractText(element, root)).map(([k, v]) => {
        if (element.textOverrides.has(k)) {
          return [v, element.textOverrides.get(k)!];
        }
        return [v, v];
      }),
    );

    const lists: SimpleElementState['lists'] = {};
    const slots: SimpleElementState['slots'] = {};
    const listSlotTypes = [
      'ST371',
      'ST370',
      'ST369',
      'ST367',
      'ST366',
      'ST365',
      'ST364',
    ];
    for (const [k, v] of element.slotOverrides.entries()) {
      const slotInfo = getSlotInfoAtNode(
        extractTreeByName(component, k),
        element.source,
        component,
      );
      const slotType = slotInfo?.typeId ?? '';
      if (listSlotTypes.includes(slotType)) {
        lists[k] = await Promise.all(
          v.items.map((el) => {
            return processElement(el);
          }),
        );
      } else {
        slots[k] = await Promise.all(v.items.map((el) => processElement(el)));
      }
    }

    return {
      name,
      texts: textMap.size > 0 ? Array.from(textMap.entries()) : undefined,
      lists: Object.keys(lists).length > 0 ? lists : undefined,
      slots: Object.keys(slots).length > 0 ? slots : undefined,
    };
  };

  return await processElement(elementState, '<root>');
}

export async function extractSimpleSectionState(
  section: SectionState,
  library: LibraryCache,
): Promise<SimpleSectionState | undefined> {
  if (section?.element.current != null) {
    return {
      element: await extractSimpleElementState(
        section.element.current,
        library,
      ),
      slug: section.element.current.source.slug,
    };
  }
}
